import { useComputedColorScheme } from "@mantine/core";
import * as Sentry from "@sentry/react";
import { useEffect, type FC, type PropsWithChildren } from "react";
import type { SWRConfiguration } from "swr";

import type { GetUserResponse } from "src/api/Tmd.dtos";
import OverlayLoading from "src/components/layouts/loading/OverlayLoading";
import { AuthenticatedUserContext } from "src/contexts/authentication/AuthenticatedUserContext";
import useAuthenticatedUser from "src/hooks/authentication/useAuthenticatedUser";
import { createSentryAnonymousUser } from "src/sentryUser";

/**
 * Configures the authenticated-user request for the enclosed application.
 */
interface IAuthenticatedUserProviderProps extends PropsWithChildren {
    configuration?: SWRConfiguration<GetUserResponse, GetUserResponse>;
}

/**
 * Loads the authenticated user, updates Sentry identity and redirects settled anonymous requests.
 *
 * Postconditions: Loading renders an overlay. When neither loading nor validating and no user is
 * available, navigation to authentication is requested with the current path/query and theme.
 * Otherwise descendants receive the current request state, even during validation without data.
 * The user effect publishes Sentry identity and resets it to anonymous on authenticated cleanup.
 * Assumptions: Browser navigation and the server own authentication; this render is not an access check.
 */
const AuthenticatedUserProvider: FC<IAuthenticatedUserProviderProps> = ({
    children,
    configuration
}) => {
    const context = useAuthenticatedUser(configuration);

    const colorScheme = useComputedColorScheme();

    useEffect(
        () => {
            if (!context.data) {
                Sentry.setUser(createSentryAnonymousUser());
                return;
            }

            Sentry.setUser({
                id: context.data.username,
                username: context.data.username,
                fullName: context.data.fullName,
                authenticated: true
            });

            return () => {
                Sentry.setUser(createSentryAnonymousUser());
            };
        },
        [context.data]
    );

    if (!context.loading && !context.validating && !context.data) {
        window.location.href = `/auth/openiddict?redirect=${encodeURIComponent(location.pathname + location.search)}&theme=${colorScheme}`;

        return <OverlayLoading />;
    } else if (context.loading) {
        return <OverlayLoading />;
    }

    return (
        <AuthenticatedUserContext.Provider value={context}>
            {children}
        </AuthenticatedUserContext.Provider>
    );
};

export default AuthenticatedUserProvider;
